// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Chips for the non-alarm row kinds, so the shared AlarmCard can render every
// tab. Routine → compliance chip (Validated / Needs review / Validating);
// optwork → work-status chip (Approved / Accepted / Declined / Pending).
// Colors are CSS classes (AlarmKindChips.module.css) selected per state,
// rather than inline style objects computed in JS.

import React from 'react';
import type { Compliance } from '../../../types/alarm';
import styles from './AlarmKindChips.module.css';

interface ChipStyle {
  label: string;
  className: string;
  /**
   * Left accent bar color for the card. Unlike the chip's own look, this is
   * consumed by a different element (AlarmCard's tile shell), so it stays a
   * plain design-token value rather than a class local to this chip.
   */
  accent: string;
}

const COMPLIANCE: Record<Compliance, ChipStyle> = {
  validated: { label: 'Validated', className: styles.validated, accent: 'var(--st-ok)' },
  attention: { label: 'Needs review', className: styles.attention, accent: 'var(--sev4)' },
  running: { label: 'Validating', className: styles.running, accent: 'var(--helios)' },
};

const WORK_STATUS: Record<string, ChipStyle> = {
  Approved: { label: 'Approved', className: styles.approved, accent: 'var(--interactive)' },
  Accepted: { label: 'Accepted', className: styles.accepted, accent: 'var(--interactive)' },
  Declined: { label: 'Declined', className: styles.declined, accent: 'var(--interactive)' },
  Pending: { label: 'Pending', className: styles.pending, accent: 'var(--interactive)' },
};

/** Compliance style descriptor for a routine (defaults to validated). */
export function complianceStyle(compliance: Compliance | undefined): ChipStyle {
  return COMPLIANCE[compliance ?? 'validated'];
}

/** Work-status style descriptor for an optimization (defaults to Pending). */
export function workStatusStyle(status: string | undefined): ChipStyle {
  return WORK_STATUS[status ?? 'Pending'] ?? WORK_STATUS.Pending;
}

function DotChip({ style }: { style: ChipStyle }) {
  return (
    <span className={`hs-kind-chip ${style.className}`}>
      <span className="hs-kind-chip-dot" />
      {style.label}
    </span>
  );
}

export function ComplianceChip({ compliance }: { compliance: Compliance | undefined }) {
  return <DotChip style={complianceStyle(compliance)} />;
}

export function WorkStatusChip({ status }: { status: string | undefined }) {
  return <DotChip style={workStatusStyle(status)} />;
}
